import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles, homepageFAQs } from '@/mocks/fanhao';
import { useState } from 'react';

export default function Home() {
  const featuredArticles = articles.slice(0, 6);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '番号查询与解析完全指南',
    url: 'https://xiuxingzhitu.cn',
    description: '专注于番号的全面解析，涵盖番号的定义、分类、查询方法及常见格式说明。',
  };

  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '番号查询与解析完全指南',
    url: 'https://xiuxingzhitu.cn',
    description: '专注于番号的全面解析，涵盖番号的定义、分类、查询方法及常见格式说明。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://xiuxingzhitu.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://xiuxingzhitu.cn/', name: '首页' } },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: homepageFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20golden%20amber%20abstract%20geometric%20pattern%20design%20with%20soft%20flowing%20lines%20representing%20information%20classification%20and%20coding%20systems%2C%20minimalist%20clean%20composition%2C%20warm%20neutral%20cream%20and%20terracotta%20color%20palette%2C%20soft%20diffused%20background%20texture%2C%20editorial%20quality%2C%20no%20text&width=1600&height=900&seq=hero-fanhao-bg&orientation=landscape"
                alt="番号查询指南背景"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/40 to-background-50"></div>
            </div>
            <div className="relative max-w-7xl mx-auto px-4 md:px-6 w-full">
              <div className="max-w-3xl">
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                  番号查询与解析
                  <span className="block text-primary-600">完全指南</span>
                </h1>
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-2xl">
                  本页专注于<strong>番号</strong>的全面解析，涵盖番号的定义、分类、查询方法及常见格式说明。无论你是初次接触番号还是资深用户，都能在此找到最权威、最实用的番号相关知识与操作指引。
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/category/format-guide"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    查看番号格式指南
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/category/manufacturer-series"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50 text-foreground-800 rounded-lg font-medium text-sm border border-background-300 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    浏览厂商番号解析
                    <i className="ri-building-2-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Category Cards */}
          <section className="py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  番号分类导航
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  按类别浏览番号知识，找到你需要的解析内容
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl p-6 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      {cat.description}
                    </p>
                    <span className="text-xs text-foreground-400">
                      {cat.articleCount} 篇文章
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                    精华文章推荐
                  </h2>
                  <p className="text-sm text-foreground-600">
                    精选最受欢迎的番号解析文章
                  </p>
                </div>
                <Link
                  to="/category/common-formats"
                  className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {article.readTime} 分钟阅读
                        </span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded whitespace-nowrap"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6 w-full">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-600">
                  关于番号查询与解析的常见疑问解答
                </p>
              </div>
              <div className="space-y-3">
                {homepageFAQs.map((faq, index) => (
                  <div
                    key={index}
                    className="border border-background-200/70 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                    >
                      <span className="font-heading text-sm font-semibold text-foreground-950 pr-4">
                        {faq.question}
                      </span>
                      <i
                        className={`ri-${
                          openFaq === index ? 'subtract' : 'add'
                        }-line text-lg text-foreground-400 flex-shrink-0 transition-transform duration-300`}
                      ></i>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6 w-full text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                还没有找到你需要的番号信息？
              </h2>
              <p className="text-sm text-foreground-600 mb-8 max-w-md mx-auto">
                浏览我们的完整分类目录，或通过搜索功能快速定位你需要的内容。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/common-formats"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览番号大全
                  <i className="ri-list-check-3"></i>
                </Link>
                <Link
                  to="/category/search-methods"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-800 rounded-lg font-medium text-sm border border-background-300 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  学习查询方法
                  <i className="ri-search-eye-line"></i>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}